import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConstants } from '@core-lib/config/app.constants';
import { ISeignalRToken, LoginResponseDto, RevokeTokenRequestDto } from '@core-lib/models/auth.model';
import { resetUserState } from '@core-lib/store/user-store/actions';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable } from 'rxjs';
import * as jwt_decode from 'jwt-decode';
import { AppCodeError, MessageFrequency } from '@core-lib/enums';

@Injectable()
export abstract class AuthSharedService {
	decodedToken: { [key: string]: string };

	private cachedSignalRToken?: string;
	private signalRTokenExpiry = 0;

	get accessToken(): string {
		return localStorage.getItem(AppConstants.LOCALSTORAGE_KEYS.accessToken);
	}

	get refreshToken(): string {
		return localStorage.getItem(AppConstants.LOCALSTORAGE_KEYS.refreshToken);
	}

	get refreshTokenExpiry(): Date {
		if (!this.refreshToken) {
			return null;
		}

		const decodedRefreshToken = jwt_decode.jwtDecode(this.refreshToken);
		const expiryTimestamp = decodedRefreshToken['exp'] * 1000; // JWT expiry is in seconds
		return new Date(expiryTimestamp);
	}

	get currentTenantKey(): string {
		return localStorage.getItem(AppConstants.LOCALSTORAGE_KEYS.tenantKey);
	}

	get isSuperAdmin(): boolean {
		return this.currentTenantKey == 'super-admin';
	}

	get authScheme(): string {
		return localStorage.getItem(AppConstants.LOCALSTORAGE_KEYS.authMode);
	}

	get isAuthenticated(): boolean {
		return !!this.accessToken;
	}

	get isRefreshTokenPresentAndValid(): boolean {
		return !!this.refreshToken;
	}

	get utilisateurId(): string {
		if (!this.accessToken) {
			return null;
		}

		const accessToken = jwt_decode.jwtDecode(this.accessToken);
		return accessToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
	}

	get messageCookiePrefix(): string {
		return `message_${this.currentTenantKey}_${this.utilisateurId}`;
	}

	constructor(
		protected store$: Store,
		protected http: HttpClient
	) {}

	public setAccessToken(accessToken: string): void {
		if (!!accessToken) {
			localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.accessToken, accessToken);
		}
	}

	public setRefreshToken(refreshToken: string): void {
		if (!!refreshToken) {
			localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.refreshToken, refreshToken);
		}
	}

	public setIdpData(idpKind: string, idpServiceIndex: string): void {
		if (!!idpKind) {
			localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.idpKind, idpKind);
		}
		if (!!idpServiceIndex) {
			localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.idpServiceIndex, idpServiceIndex);
		}
	}

	public setTenantKey(tenantKey: string): void {
		localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.tenantKey, tenantKey);
	}

	public setAuthScheme(authMode: string): void {
		localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.authMode, authMode);
	}

	public setOidcMode(oidcMode: string): void {
		localStorage.setItem(AppConstants.LOCALSTORAGE_KEYS.oidcMode, oidcMode);
	}

	public getOidcMode(): string {
		return localStorage.getItem(AppConstants.LOCALSTORAGE_KEYS.oidcMode);
	}

	public decodeToken(jwtToken: string) {
		if (jwtToken) {
			this.decodedToken = jwt_decode.jwtDecode(jwtToken);
		}
	}

	public decodeJwtToken(jwtToken: string): { [key: string]: string } {
		return jwt_decode.jwtDecode(jwtToken);
	}

	// Garder les 2 paramètres mêmes s'ils ne sont pas utilisés ici
	// Ils sont utilisés par les classes dérivées
	logout(revokeRefreshToken: boolean = false, codeError?: AppCodeError): void {
		this.dispose();
	}

	forbidden(): void {
		this.logout(); // TODO: Implement forbidden logic
	}

	dispose(resetState: boolean = true) {
		this.clearMessageCookies();

		localStorage.removeItem(AppConstants.LOCALSTORAGE_KEYS.accessToken);
		localStorage.removeItem(AppConstants.LOCALSTORAGE_KEYS.refreshToken);
		localStorage.removeItem(AppConstants.LOCALSTORAGE_KEYS.tenantKey);
		localStorage.removeItem(AppConstants.LOCALSTORAGE_KEYS.authMode);
		localStorage.removeItem(AppConstants.LOCALSTORAGE_KEYS.oidcMode);

		if (resetState) {
			this.store$.dispatch(resetUserState());
		}

		localStorage.clear();
	}

	private clearMessageCookies(): void {
		const cookies = document.cookie.split(';');
		const cookiePrefix = `${this.messageCookiePrefix}_${MessageFrequency.always}_`;

		cookies.forEach(cookie => {
			const cookieName = cookie.split('=')[0].trim();
			if (cookieName.startsWith(cookiePrefix)) {
				document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
			}
		});
	}

	getAuthTokens(tempToken: string): Observable<LoginResponseDto> {
		return this.http.post<LoginResponseDto>(`${environment.apiUrl}/auth/tokens`, {
			tempToken: tempToken
		});
	}

	doRefreshToken(): Observable<LoginResponseDto> {
		return this.http.post<LoginResponseDto>(`${environment.apiUrl}/auth/refresh-token`, {
			accessToken: this.accessToken,
			refreshToken: this.refreshToken
		});
	}

	revokeToken(refreshToken: string): Observable<any> {
		const request: RevokeTokenRequestDto = {
			token: refreshToken
		};
		return this.http.post<any>(`${environment.apiUrl}/auth/revoke-refresh-token`, request);
	}

	async getSignalRTokenCached(): Promise<string> {
		if (this.cachedSignalRToken && Date.now() < this.signalRTokenExpiry) {
			return this.cachedSignalRToken;
		}

		const response = await firstValueFrom(this.http.get<ISeignalRToken>(`${environment.apiUrl}/auth/signalr/token`));

		this.cachedSignalRToken = response.token;
		this.signalRTokenExpiry = Date.now() + 50000;

		return this.cachedSignalRToken;
	}
}
